import { DataTableColumn } from '@shared/ui/data-table';
import { useCallback, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { DataTableRow } from './DataTableRow';
import { styles } from './styles';

interface RowsProps<T extends { id?: string }> {
  rows: T[];
  columns: DataTableColumn<T>[];
  gridTemplateColumns: string;
  isLoading?: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
  selectedRows?: Array<string | number>;
  selectedClassName?: string;
}

export const Rows = <T extends { id?: string }>({
  rows,
  columns,
  gridTemplateColumns,
  isLoading,
  hasMore,
  loadMore,
  selectedRows,
  selectedClassName,
}: RowsProps<T>) => {
  const observerRef = useRef<IntersectionObserver>();

  const lastEventElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore?.();
        }
      });

      if (node) {
        observerRef.current.observe(node);
      }
    },
    [isLoading, hasMore, loadMore]
  );

  return (
    <div {...stylex.props(styles.rowsContainer)}>
      {rows.map((row, rowIndex) => (
        <DataTableRow
          key={row.id || rowIndex}
          row={row}
          rowIndex={rowIndex}
          columns={columns}
          gridTemplateColumns={gridTemplateColumns}
          isLastRow={rowIndex === rows.length - 1}
          lastElementRef={lastEventElementRef}
          isSelected={!!(row.id && selectedRows?.includes(row.id))}
          selectedClassName={selectedClassName}
        />
      ))}
    </div>
  );
};
